import { ExclamationCircleFilled } from '@ant-design/icons';
import type { WorkHoursValues } from '@components/WorkHours';
import WorkHours from '@components/WorkHours';
import { DefaultWorkHours, isOpen24x7 } from '@components/WorkHours/WorkHours';
import type { OrganizationSettings } from '@interfaces/organization-settings';
import { mapEmptyToNull } from '@pages/Management/hooks/use-editable-form';
import { ACTIONS } from '@pages/Management/OrganizationManagement/action';
import type { LoaderData } from '@services/types/loader-data';
import { Button, Form, Input, Modal, Select, Switch } from 'antd';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetcher, useLoaderData } from 'react-router-dom';

import type { loader } from './loader';

const ALLOWED_CONVERSATION_DEACTIVATION_THRESHOLDS = [1, 2, 3, 6, 12];

interface SettingsForm {
  off_hours_response: string | null;
  automatic_introduction: boolean;
  receipt_for_all: boolean;
  short_forwarding_title: boolean;
  work_hours?: WorkHoursValues;
  conversation_deactivation_threshold_months: number | null;
}

const OrganizationManagement: FC = () => {
  const settings = useLoaderData() as LoaderData<typeof loader>;
  const [form] = Form.useForm<SettingsForm>();
  const { submit, state } = useFetcher<Response | OrganizationSettings>();
  const [modal, contextHolder] = Modal.useModal();
  const isDisabled = useMemo(() => state === 'submitting' || state === 'loading', [state]);
  const [submittable, setSubmittable] = useState(false);

  // Watch all values
  const values = Form.useWatch([], form);

  useEffect(() => {
    // TODO: inspect why it doesn't work
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(form.isFieldsTouched());
      },
      () => {
        setSubmittable(false);
      },
    );
  }, [form, values]);

  useEffect(() => {
    form.setFieldsValue({
      off_hours_response: settings?.off_hours_response || null,
      automatic_introduction: settings?.automatic_introduction ?? true,
      receipt_for_all: settings?.receipt_for_all ?? false,
      short_forwarding_title: settings?.short_forwarding_title ?? false,
      work_hours: settings?.work_hours ?? undefined,
      conversation_deactivation_threshold_months: settings?.conversation_deactivation_threshold_months ?? null,
    });
  }, [settings, form]);

  const showClearConfirm = useCallback(() => {
    modal.confirm({
      title: 'Clear work-hours settings?',
      icon: <ExclamationCircleFilled />,
      content: 'This will reset all work hours and remove the off-hours auto-response.',
      okText: 'Clear',
      okButtonProps: { danger: true },
      onOk: () => {
        const data: Omit<OrganizationSettings, 'id'> & { intent: ACTIONS } = {
          intent: ACTIONS.Update,
          work_hours: DefaultWorkHours,
          off_hours_response: null,
          automatic_introduction: settings.automatic_introduction ?? true,
          receipt_for_all: settings.receipt_for_all ?? false,
        };
        submit(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          data,
          {
            method: 'patch',
            encType: 'application/json',
          },
        );
      },
    });
  }, [modal, settings.automatic_introduction, settings.receipt_for_all, submit]);

  const onFinish = useCallback(
    (values: SettingsForm) => {
      if (settings === undefined) {
        return;
      }

      const initialSettings = settings;
      // Set conversation_deactivation_threshold_months to null explicitly when not set (fix for clear)
      values.conversation_deactivation_threshold_months = values.conversation_deactivation_threshold_months ?? null;

      const changedValues = Object.fromEntries(
        Object.entries(values)
          .filter(([key, value]) => initialSettings[key as keyof OrganizationSettings] !== value && value !== undefined)
          .map(mapEmptyToNull),
      );

      try {
        if (Object.keys(changedValues).length > 0) {
          submit(
            {
              intent: 'update-organization',
              ...changedValues,
            },
            {
              method: 'patch',
              encType: 'application/json',
            },
          );
        }
      } catch (error) {
        console.error(`Error updating the work hours settings:`, error);
      }
    },
    [settings, submit],
  );

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  const tailLayout = {
    wrapperCol: { offset: 8, span: 16 },
  };

  return (
    <Form<SettingsForm> form={form} {...layout} labelWrap style={{ maxWidth: 800 }} onFinish={onFinish}>
      <Form.Item
        label="Work hours"
        name="work_hours"
        tooltip="Defines when your organization is open and when the off-hours response can be sent. Times use the selected timezone."
      >
        <WorkHours />
      </Form.Item>
      <Form.Item
        label="Off-hours auto-response"
        name="off_hours_response"
        dependencies={['work_hours']}
        tooltip="Sent outside work hours, at most once per group per hour. Organization admins do not receive it."
        rules={[
          ({ getFieldValue }) => ({
            message: 'An off-hours response message is required unless your organization is open 24/7.',
            validator: (rule, value) => {
              const work_hours = getFieldValue('work_hours');
              if (isOpen24x7(work_hours)) {
                return Promise.resolve();
              }

              if (value === undefined || value === null || (typeof value === rule.type && value.length === 0)) {
                return Promise.reject(new Error());
              }

              return Promise.resolve();
            },
          }),
        ]}
      >
        <Input.TextArea placeholder="Enter the response sent outside work hours" disabled={isDisabled} rows={6} />
      </Form.Item>
      <Form.Item
        label="Automatically register new users"
        name="automatic_introduction"
        valuePropName="checked"
        tooltip="Adds new Telegram group members without prompting them. When disabled, unknown users must introduce themselves when they first message the bot."
      >
        <Switch disabled={isDisabled} />
      </Form.Item>
      <Form.Item
        label="Share read status with the team"
        name="receipt_for_all"
        valuePropName="checked"
        tooltip="When one team member replies or changes a conversation's read status, the change applies to everyone in the organization."
      >
        <Switch disabled={isDisabled} />
      </Form.Item>
      <Form.Item
        label="Use short format for forwarded service requests"
        name="short_forwarding_title"
        valuePropName="checked"
        tooltip='Short format omits the "Forwarded from" label, agent signature, and deep message or group links.'
      >
        <Switch disabled={isDisabled} />
      </Form.Item>
      <Form.Item
        name="conversation_deactivation_threshold_months"
        label="Revoke inactive conversations after"
        tooltip="Automatically revokes authorization when a conversation's latest message is older than the selected period."
        help="Choose Never to disable automatic revocation."
      >
        <Select
          allowClear
          placeholder="Never"
          options={ALLOWED_CONVERSATION_DEACTIVATION_THRESHOLDS.map((months) => ({
            value: months,
            label: `${months} month${months === 1 ? '' : 's'}`,
          }))}
        />
      </Form.Item>
      <Form.Item {...tailLayout} style={{ marginTop: '2rem' }}>
        <Button type="primary" htmlType="submit" loading={isDisabled} disabled={isDisabled || !submittable}>
          Save changes
        </Button>
        <Button danger onClick={showClearConfirm} style={{ marginLeft: '10px' }} disabled={isDisabled}>
          Clear work hours
        </Button>
      </Form.Item>
      {contextHolder}
    </Form>
  );
};

export default OrganizationManagement;
